// invoiceValidation.ts
import * as Yup from "yup";
import type { TFunction } from "i18next";
import type { Invoice, CreditNote, Offer, IdWithNameWithEmail } from "./CommercialDocumentTypes";
import type { IdWithName } from "@repo/shared/common";
import { DocumentType, DocumentStatus, CreditNoteKind, DocumentRole } from "@repo/server-api";

import { PayData } from "../dialogs/PaymentRecordForwardRefDialog";

// --- Helper ------------------------------------------------------------------

const requiredString = (t: TFunction, key: string) =>
	Yup.string()
		.transform((value) => (typeof value === "string" ? value.trim() : value))
		.min(1, t(key, { ns: "validation" }))
		.required(t(key, { ns: "validation" }));

const nullableNumber = () => Yup.number().nullable().defined();

const nullableString = () =>
	Yup.string()
		.transform((value, originalValue) => {
			// string | null → allow null OR any string (including empty)
			if (originalValue === null || originalValue === undefined) {
				return null;
			}
			return typeof originalValue === "string" ? originalValue : String(originalValue);
		})
		.nullable();

const requiredNumber = (t: TFunction, key: string) =>
	Yup.number()
		.typeError(t(key, { ns: "validation" }))
		.required(t(key, { ns: "validation" }));

// --- Schema Factory ----------------------------------------------------------

export const createIdWithNameSchema = (t: TFunction): Yup.ObjectSchema<IdWithName> =>
	Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
	});

export const createInvoiceSchema = (t: TFunction): Yup.ObjectSchema<Invoice> => {
	const idWithNameSchema: Yup.ObjectSchema<IdWithName> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
	});

	const idWithNameWithEmailSchema: Yup.ObjectSchema<IdWithNameWithEmail> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
		email: requiredString(t, "email.required"),
	});

	// root Invoice schema
	const invoiceSchema: Yup.ObjectSchema<Invoice> = Yup.object({
		id: nullableString(),
		number: nullableString(),
		project: idWithNameSchema.required(t("project-is-required", { ns: "validation" })),
		client: idWithNameSchema.required(t("client-is-required", { ns: "validation" })),
		contactPerson: idWithNameWithEmailSchema.required(
			t("contact-person-is-required", { ns: "validation" }),
		),
		contractor: idWithNameSchema.required(t("contractor-is-required", { ns: "validation" })),
		leftTotal: nullableNumber(),
		grossTotal: nullableNumber(),
		issueDate: nullableString(),
		dueDate: nullableString(),
		status: Yup.mixed<DocumentStatus>().required(t("status-is-required", { ns: "validation" })),
		type: Yup.mixed<DocumentType>().required(t("type-is-required", { ns: "validation" })),
		paymentDeadlineDays: requiredNumber(t, "payment-deadline-days-is-required"),
		headerNote: nullableString(),
		footerNote: nullableString(),
		items: Yup.array()
			.of(
				Yup.object({
					id: nullableString(),
					description: nullableString(),
					name: requiredString(t, "item-name-is-required"),
					quantity: requiredNumber(t, "item-quantity-is-required"),
					unitPrice: requiredNumber(t, "item-unit-price-is-required"),
					unit: requiredString(t, "item-unit-is-required"),
					discount: requiredNumber(t, "item-discount-is-required"),
					taxCode: requiredString(t, "item-tax-code-is-required"),
					totalPrice: requiredNumber(t, "item-total-price-is-required"),
					tax: requiredNumber(t, "item-tax-is-required"),
				}),
			)
			.min(1, t("at-least-one-item-is-required", { ns: "validation" }))
			.required(t("at-least-one-item-is-required", { ns: "validation" })),
		notes: nullableString(),
		netTotal: nullableNumber(),
		isProforma: Yup.boolean().optional(),
		isInvalid: Yup.boolean().optional(),
		sentDate: nullableString(),
	});

	return invoiceSchema;
};

export const createCreditNoteSchema = (t: TFunction): Yup.ObjectSchema<CreditNote> => {
	const idWithNameSchema: Yup.ObjectSchema<IdWithName> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
	});

	const idWithNameWithEmailSchema: Yup.ObjectSchema<IdWithNameWithEmail> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
		email: requiredString(t, "email.required"),
	});

	// CreditNote schema - contactPerson is optional and only IdWithName (no email)
	const creditNoteSchema: Yup.ObjectSchema<CreditNote> = Yup.object({
		id: nullableString(),
		number: nullableString(),
		client: idWithNameSchema.required(t("supplier-is-required", { ns: "validation" })),
		project: idWithNameSchema.required(t("project-is-required", { ns: "validation" })),
		contactPerson: idWithNameWithEmailSchema.required(
			t("contact-person-is-required", { ns: "validation" }),
		),
		contractor: idWithNameSchema.required(t("contractor-is-required", { ns: "validation" })),
		leftTotal: nullableNumber(),
		grossTotal: nullableNumber(),
		issueDate: nullableString(),
		dueDate: nullableString(),
		status: Yup.mixed<DocumentStatus>().required(t("status-is-required", { ns: "validation" })),
		type: Yup.mixed<DocumentType>().required(t("type-is-required", { ns: "validation" })),
		paymentDeadlineDays: requiredNumber(t, "payment-deadline-days-is-required"),
		headerNote: nullableString(),
		footerNote: nullableString(),
		items: Yup.array()
			.of(
				Yup.object({
					id: nullableString(),
					description: nullableString(),
					name: requiredString(t, "item-name-is-required"),
					quantity: requiredNumber(t, "item-quantity-is-required"),
					unitPrice: requiredNumber(t, "item-unit-price-is-required"),
					unit: requiredString(t, "item-unit-is-required"),
					discount: requiredNumber(t, "item-discount-is-required"),
					taxCode: requiredString(t, "item-tax-code-is-required"),
					totalPrice: requiredNumber(t, "item-total-price-is-required"),
					tax: requiredNumber(t, "item-tax-is-required"),
				}),
			)
			.min(1, t("at-least-one-item-is-required", { ns: "validation" }))
			.required(t("at-least-one-item-is-required", { ns: "validation" })),
		notes: nullableString(),
		netTotal: nullableNumber(),
		// CreditNote-specific fields
		kind: Yup.mixed<CreditNoteKind>().required(),
		documentRole: Yup.mixed<DocumentRole>().required(),
		settlesInvoiceId: nullableString(),
		isProforma: Yup.boolean().optional(),
		isInvalid: Yup.boolean().optional(),
		sentDate: nullableString(),
	});

	return creditNoteSchema;
};

export const createOfferSchema = (t: TFunction): Yup.ObjectSchema<Offer> => {
	const idWithNameSchema: Yup.ObjectSchema<IdWithName> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
	});

	const idWithNameWithEmailSchema: Yup.ObjectSchema<IdWithNameWithEmail> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
		email: requiredString(t, "email.required"),
	});

	return Yup.object({
		id: nullableString(),
		number: nullableString(),
		client: idWithNameSchema.required(t("client-is-required", { ns: "validation" })),
		project: idWithNameSchema.required(t("project-is-required", { ns: "validation" })),
		contactPerson: idWithNameWithEmailSchema.required(
			t("contact-person-is-required", { ns: "validation" }),
		),
		contractor: idWithNameSchema.required(t("contractor-is-required", { ns: "validation" })),
		headerNote: nullableString(),
		footerNote: nullableString(),
		items: Yup.array()
			.of(
				Yup.object({
					id: nullableString(),
					description: nullableString(),
					name: requiredString(t, "item-name-is-required"),
					quantity: requiredNumber(t, "item-quantity-is-required"),
					unitPrice: requiredNumber(t, "item-unit-price-is-required"),
					unit: requiredString(t, "item-unit-is-required"),
					discount: requiredNumber(t, "item-discount-is-required"),
					taxCode: requiredString(t, "item-tax-code-is-required"),
					totalPrice: requiredNumber(t, "item-total-price-is-required"),
					tax: requiredNumber(t, "item-tax-is-required"),
				}),
			)
			.min(1, t("at-least-one-item-is-required", { ns: "validation" }))
			.required(t("at-least-one-item-is-required", { ns: "validation" })),
		issueDate: nullableString(),
		type: Yup.mixed<DocumentType>().nullable().defined(),
		status: Yup.mixed<DocumentStatus>().optional(),
		isProforma: Yup.boolean().optional(),
		paymentDeadlineDays: nullableNumber(),
		dueDate: nullableString(),
	});
};

export const createInvoicePaymentSchema = (
	t: TFunction,
	remainingAmount?: number,
): Yup.ObjectSchema<PayData> => {
	let amountSchema = requiredNumber(t, "payment-amount-is-required").test(
		"not-zero",
		t("payment-amount-must-not-be-zero", { ns: "validation" }),
		(value) => value !== undefined && value !== 0,
	);

	if (remainingAmount !== undefined && remainingAmount > 0) {
		amountSchema = amountSchema.max(
			remainingAmount,
			t("payment-amount-exceeds-remaining", {
				ns: "validation",
				remaining: remainingAmount.toFixed(2),
			}),
		);
	} else if (remainingAmount !== undefined && remainingAmount < 0) {
		amountSchema = amountSchema.min(
			remainingAmount,
			t("payment-amount-exceeds-remaining", {
				ns: "validation",
				remaining: remainingAmount.toFixed(2),
			}),
		);
	}

	const invoicePaymentSchema: Yup.ObjectSchema<PayData> = Yup.object({
		amount: amountSchema,
		paymentDate: requiredString(t, "payment-date-is-required"),
		notes: nullableString(),
		paymentMethod: nullableString(),
	});

	return invoicePaymentSchema;
};
